import { demoFunc } from './dees-harness-session-list.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
  directives,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessActivityStyles } from '../harness.styles.js';
import type {
  IDropSlot,
  IHarnessGroupContextDetail,
  IHarnessItemMoveDetail,
  IHarnessResourceAssociation,
  IHarnessResourceContextDetail,
  IHarnessResourceEventDetail,
  IHarnessResourceMeta,
  IHarnessSessionContextDetail,
  IHarnessSessionEventDetail,
  IHarnessSessionGroup,
  IHarnessSessionListItemRef,
  IHarnessSessionMeta,
  THarnessItemMoveSource,
  THarnessResourceAssociationInteractionSource,
  THarnessResourceAssociateDetail,
  THarnessResourceAssociateMode,
  THarnessResourceContextSource,
} from '../interfaces.js';
import { harnessFormatRelativeTime } from '../harness.helpers.js';
import '../../00group-utility/dees-icon/dees-icon.js';

const { repeat } = directives;
const dragAutoScrollMaxPixelsPerSecond = 900;
const dragThresholdPixels = 4;
/**
 * Share of a row's height, centred, that attaches instead of inserting: the centre half of the
 * row attaches and the outer quarters reposition, so reordering a resource past a conversation
 * never turns into an attachment.
 */
const attachCentreBandRatio = 0.5;

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-session-list': DeesHarnessSessionList;
  }
}

/** One ordered row. Sessions and resources are peers in a single order. */
type TListItem =
  | {
      kind: 'session';
      key: string;
      id: string;
      title: string;
      sortable: boolean;
      timestamp: number;
      session: IHarnessSessionMeta;
    }
  | {
      kind: 'resource';
      key: string;
      id: string;
      title: string;
      sortable: boolean;
      timestamp: number;
      resource: IHarnessResourceMeta;
    };

const itemKey = (refArg: IHarnessSessionListItemRef): string => `${refArg.kind}:${refArg.id}`;

const itemRef = (itemArg: IHarnessSessionListItemRef): IHarnessSessionListItemRef => ({
  kind: itemArg.kind,
  id: itemArg.id,
});

const sameItem = (
  leftArg: IHarnessSessionListItemRef | undefined | null,
  rightArg: IHarnessSessionListItemRef | undefined | null,
): boolean => !!leftArg
  && !!rightArg
  && leftArg.kind === rightArg.kind
  && leftArg.id === rightArg.id;

interface IPointerDragGesture {
  pointerId: number;
  handle: HTMLElement;
  source: HTMLElement;
  item: IHarnessSessionListItemRef;
  sourceGroupId: string | null;
  sourceLogicalIndex: number;
  startClientX: number;
  startClientY: number;
  grabOffsetX: number;
  grabOffsetY: number;
  phase: 'pending' | 'active';
}

interface IPendingResourceAssociationRequest {
  resourceId: string;
  resourceTitle: string;
  fromTarget: IHarnessSessionListItemRef | null;
  toTarget: IHarnessSessionListItemRef | null;
  mode: THarnessResourceAssociateMode;
  /** The target set the request would produce, compared against the controlled set on update. */
  expectedTargets: IHarnessSessionListItemRef[];
}

interface IKeyboardMoveFocus {
  item: IHarnessSessionListItemRef;
  groupId: string | null;
}

type TSectionRenderEntry =
  | { type: 'item'; key: string; item: TListItem; logicalIndex: number }
  | { type: 'placeholder'; key: string };

/**
 * Searchable conversation list, embeddable in a sidebar or a DeesModal.
 * Emits `harness-session-select` on click, `harness-session-open` on
 * double-click or Enter, and `harness-session-context` with viewport
 * coordinates on right-click, Actions, or ContextMenu/Shift+F10.
 *
 * Sessions and host resources are peers in ONE ordered list: a resource row
 * can be dragged anywhere a session can, including between sessions and into
 * or out of groups. A resource can also be attached to several items at once.
 * Attaching happens either through `requestResourceAssociation()` from a host
 * menu, or by releasing a resource drag on the centre half of an eligible row;
 * the outer quarters of that row, and every gap between rows, stay purely
 * positional. Attaching never moves a row, and the association renders only as
 * markers on the resource row. Every change is requested through
 * `harness-resource-associate`; the host owns the controlled set.
 *
 * The component never mutates its inputs: reordering emits `harness-item-move`,
 * the "new group" button emits `harness-group-create-request`, and
 * right-clicking a group header emits `harness-group-context` — the host
 * persists changes and passes fresh `groups` / `ungroupedItemIds` back in.
 * Items in no group render in the trailing ungrouped section with pinned
 * entries first, then explicit order, then new entries by recency. With
 * `enableGrouping` the list additionally renders group chrome. While a search
 * query is active the list is one flat, non-sortable, recency-ordered section
 * in which sessions and resources match as peers.
 */
@customElement('dees-harness-session-list')
export class DeesHarnessSessionList extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor sessions: IHarnessSessionMeta[] = [];

  @property()
  accessor selectedSessionId: string = '';

  @property({ type: Boolean })
  accessor loading: boolean = false;

  @property()
  accessor emptyText: string = 'No conversations yet.';

  @property({ attribute: false })
  accessor groups: IHarnessSessionGroup[] = [];

  @property({ type: Boolean })
  accessor enableGrouping: boolean = false;

  /** Explicit order for sortable items of both kinds outside user-defined groups. */
  @property({ attribute: false })
  accessor ungroupedItemIds: IHarnessSessionListItemRef[] = [];

  @property({ attribute: false })
  accessor resources: IHarnessResourceMeta[] = [];

  @property({ attribute: false })
  accessor resourceAssociations: IHarnessResourceAssociation[] = [];

  @property()
  accessor selectedResourceId = '';

  /**
   * Serializable association targets, honoured by host menus and by the drop
   * affordance alike: an ineligible row offers no attachment and commits
   * nothing. A missing entry leaves the resource unrestricted; an empty array
   * means no eligible target.
   */
  @property({ attribute: false })
  accessor eligibleTargetsByResourceId: Record<string, IHarnessSessionListItemRef[]> = {};

  @state()
  accessor search: string = '';

  @state()
  accessor draggingItem: IHarnessSessionListItemRef | undefined = undefined;

  @state()
  accessor dropSlot: IDropSlot | undefined = undefined;

  /**
   * The row a resource drag would attach to. Mutually exclusive with `dropSlot`: a pointer is
   * either inserting between rows or attaching to one, never both.
   */
  @state()
  accessor dropAttachTarget: IHarnessSessionListItemRef | undefined = undefined;

  @state()
  accessor draggedItemHeight = 0;

  @state()
  accessor expandedSessionIds: ReadonlySet<string> = new Set<string>();

  @state()
  accessor collapsedGroupIds: ReadonlySet<string> = new Set<string>();

  @state()
  private accessor topScrollShadowVisible = false;

  @state()
  private accessor bottomScrollShadowVisible = false;

  @state()
  private accessor moveAnnouncement = '';

  @state()
  private accessor resourceContextResourceId = '';

  private reflowGeneration = 0;
  private readonly reflowAnimations = new Set<Animation>();
  private pointerDragGesture: IPointerDragGesture | undefined;
  private pendingResourceAssociationRequest: IPendingResourceAssociationRequest | undefined;
  private resourceContextFocusTarget: HTMLElement | undefined;
  private resourceContextFocusClass = '';
  private resourceContextGeneration = 0;
  private keyboardMoveFocus: IKeyboardMoveFocus | undefined;
  private scrollResizeObserver: ResizeObserver | undefined;
  private observedScrollContainer: HTMLElement | undefined;
  private observedScrollContent: HTMLElement | undefined;
  private dragAutoScrollFrame: number | undefined;
  private dragAutoScrollClientX = 0;
  private dragAutoScrollClientY = 0;
  private dragAutoScrollVelocity = 0;
  private dragAutoScrollLastFrame = 0;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessActivityStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        min-height: 0;
        font-family: var(--dees-font-family);
        --session-normal: var(--dees-color-text-muted);
        --session-working: var(--dees-color-link);
        --session-external: var(--dees-color-text-warning);
        --session-finished: var(--dees-color-text-success);
        --session-attention: var(--dees-color-text-warning);
        --session-error: var(--dees-color-text-error);
      }

      .searchRow {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        flex: 0 0 auto;
        padding: var(--dees-spacing-sm) var(--dees-spacing-md) var(--dees-spacing-md);
      }

      .searchField {
        position: relative;
        flex: 1;
        min-width: 0;
      }

      .searchIcon {
        position: absolute;
        top: 50%;
        left: 10px;
        color: var(--dees-color-text-muted);
        transform: translateY(-50%);
        pointer-events: none;
      }

      .searchRow input {
        display: block;
        width: 100%;
        height: var(--dees-control-height-lg);
        padding: 0 var(--dees-spacing-md) 0 34px;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-control-bg);
        color: var(--dees-color-text-primary);
        font-family: inherit;
        font-size: var(--dees-font-control-size-sm, 12px);
        outline: none;
        box-sizing: border-box;
        transition: border-color var(--dees-transition-fast) var(--dees-ease-standard),
          box-shadow var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .searchRow input:focus {
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      .list {
        width: 100%;
        height: 100%;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
        padding: 0 var(--dees-spacing-md) var(--dees-spacing-md);
        box-sizing: border-box;
      }

      .listFrame {
        position: relative;
        flex: 1;
        min-height: 0;
      }

      /* Drag scrolling owns the viewport while drop placeholders move. */
      .list.dragActive {
        overflow-anchor: none;
      }

      .scrollShadow {
        position: absolute;
        z-index: 1;
        left: 0;
        right: 0;
        height: 16px;
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .scrollShadow.visible {
        opacity: 1;
      }

      .scrollShadow.top {
        top: 0;
        background: linear-gradient(
          to bottom,
          var(--dees-color-scroll-edge-shadow),
          color-mix(in srgb, var(--dees-color-scroll-edge-shadow) 35%, transparent) 6px,
          transparent 100%
        );
      }

      .scrollShadow.bottom {
        bottom: 0;
        /* Paint inward from the boundary; an outer box shadow would cast
           from the top of this band and appear to float above the footer. */
        background: linear-gradient(
          to top,
          var(--dees-color-scroll-edge-shadow),
          color-mix(in srgb, var(--dees-color-scroll-edge-shadow) 35%, transparent) 6px,
          transparent 100%
        );
      }

      .section {
        position: relative;
        display: grid;
        gap: var(--dees-spacing-sm);
        align-content: start;
      }

      .item {
        --card-accent: var(--session-normal);
        --card-inner-background: var(--dees-color-bg-secondary);
        --card-inner-border: var(--dees-color-border-subtle);
        position: relative;
        width: 100%;
        min-width: 0;
        overflow: hidden;
        border: 0;
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: transparent;
        color: var(--dees-color-text-primary);
        box-sizing: border-box;
      }

      .item.selected {
        --card-inner-background: var(--dees-color-nav-selected);
        --card-inner-border: color-mix(in srgb, var(--dees-color-accent-primary) 35%, transparent);
      }

      .itemInner {
        position: relative;
        isolation: isolate;
        min-width: 0;
        overflow: hidden;
        border: 1px solid var(--card-inner-border);
        border-radius: calc(var(--dees-radius-xl) - 1px);
        corner-shape: var(--dees-corner-shape);
        background: var(--card-inner-background);
        box-sizing: border-box;
        transition:
          background var(--dees-transition-fast) var(--dees-ease-standard),
          border-color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      /* One drag preview for both item kinds. */
      .item.dragging,
      .resourceRow.dragging {
        position: fixed;
        inset: 0 auto auto 0;
        width: var(--drag-preview-width);
        height: var(--drag-preview-height);
        max-width: none;
        margin: 0;
        padding: 0;
        opacity: 0.96;
        pointer-events: none;
        transform: translate3d(var(--drag-preview-x), var(--drag-preview-y), 0);
        box-shadow: var(--dees-shadow-xl);
      }

      .item.state-working {
        --card-accent: var(--session-working);
      }

      .item.state-external {
        --card-accent: var(--session-external);
      }

      .item.state-external .itemStatus {
        font-weight: 400;
      }

      .item.active .itemHeader .stateDot {
        animation: harness-activity-pulse 1.6s ease-in-out infinite;
      }

      .item.state-finished {
        --card-accent: var(--session-finished);
      }

      .item.state-finished .itemStatus {
        color: var(--dees-color-text-secondary);
      }

      .item.state-attention {
        --card-accent: var(--session-attention);
      }

      .item.state-error {
        --card-accent: var(--session-error);
      }

      .item.state-attention .itemInner,
      .item.state-error .itemInner {
        box-shadow: inset 3px 0 var(--card-accent);
      }

      .itemHeader {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 30px 30px;
        min-height: 44px;
      }

      .itemHeader.draggable {
        grid-template-columns: 24px minmax(0, 1fr) 30px 30px;
      }

      .dragHandle {
        appearance: none;
        display: grid;
        place-items: center;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--dees-color-text-muted);
        cursor: grab;
        touch-action: none;
      }

      .dragHandle:active {
        cursor: grabbing;
      }

      .dragHandle:focus-visible {
        outline: 2px solid var(--dees-color-accent-primary);
        outline-offset: -3px;
        border-radius: 8px;
      }

      .itemPrimary,
      .sessionActions,
      .expandToggle {
        appearance: none;
        border: 0;
        background: transparent;
        color: inherit;
        font-family: inherit;
      }

      .itemPrimary {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: 6px var(--dees-spacing-xs) 6px var(--dees-spacing-md);
        text-align: left;
        cursor: pointer;
      }

      .itemPrimary:focus-visible,
      .sessionActions:focus-visible,
      .expandToggle:focus-visible {
        outline: 2px solid var(--dees-color-accent-primary);
        outline-offset: -3px;
        border-radius: 9px;
      }

      .stateDot {
        display: grid;
        place-items: center;
        width: 16px;
        height: 16px;
        flex: 0 0 auto;
        color: var(--card-accent);
      }

      .itemCopy {
        display: grid;
        gap: 2px;
        min-width: 0;
        flex: 1;
      }

      .itemStatus {
        color: var(--card-accent);
        font-size: var(--dees-font-caption2-size, 11px);
        font-weight: 560;
        line-height: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .itemIdentity {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: 5px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption2-size, 10px);
        line-height: 14px;
      }

      .harnessName,
      .projectName {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .itemIdentity dees-icon { flex: 0 0 auto; }

      .itemTitle {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: 6px;
        font-size: var(--dees-font-control-size-sm, 12px);
        font-weight: 590;
      }

      .itemTitle .titleText {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .itemTitle .itemIcon {
        flex: 0 0 auto;
        color: var(--dees-color-text-secondary);
      }

      .itemTime {
        flex: 0 0 auto;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
      }

      .sessionActions,
      .expandToggle {
        display: grid;
        place-items: center;
        min-width: 27px;
        min-height: 32px;
        margin: 7px 3px 7px 0;
        padding: 0;
        border-radius: 9px;
        color: var(--dees-color-text-muted);
        cursor: pointer;
      }

      .expandToggle dees-icon {
        transition: transform var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .item.expanded .expandToggle dees-icon {
        transform: rotate(180deg);
      }

      .itemDetails {
        display: grid;
        gap: 6px;
        margin: 0 10px 10px 28px;
        padding: 9px 10px;
        border-top: 1px solid color-mix(in srgb, var(--card-accent) 20%, var(--dees-color-border-subtle));
        color: var(--dees-color-text-secondary);
      }

      .itemPreview {
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .itemMeta {
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
      }

      /* Resource rows are peers of session cards in the same ordered section. */
      .resourceRow {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1fr) 38px;
        min-width: 0;
        overflow: hidden;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: color-mix(in srgb, var(--dees-color-bg-secondary) 76%, transparent);
        color: var(--dees-color-text-primary);
        box-shadow: var(--dees-shadow-xs);
        box-sizing: border-box;
      }

      .resourceRow.draggable {
        grid-template-columns: 26px minmax(0, 1fr) 38px;
      }

      .resourceRow.selected {
        border-color: var(--dees-color-accent-primary);
        background: color-mix(in srgb, var(--dees-color-accent-primary) 10%, var(--dees-color-bg-secondary));
      }

      .resourceDragHandle,
      .resourcePrimary,
      .resourceActions {
        appearance: none;
        border: 0;
        background: transparent;
        color: inherit;
        font-family: inherit;
      }

      .resourceDragHandle {
        min-width: 26px;
      }

      .resourcePrimary {
        display: flex;
        min-width: 0;
        min-height: 38px;
        align-items: center;
        gap: 7px;
        padding: 5px 4px 5px 7px;
        text-align: left;
        cursor: pointer;
      }

      .resourceIcon {
        flex: 0 0 auto;
        color: var(--dees-color-text-secondary);
      }

      .resourceCopy {
        min-width: 0;
        flex: 1;
      }

      .resourceTitle {
        display: block;
        overflow: hidden;
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-caption1-size, 11px);
        font-weight: 620;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .resourcePreview {
        display: block;
        overflow: hidden;
        margin-top: 1px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption2-size, 10px);
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .resourceMeta {
        display: block;
        overflow: hidden;
        margin-top: 1px;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Decorative markers for the controlled associations; never interactive. */
      .resourceAttachments {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: 4px;
        margin-top: 2px;
        color: var(--dees-color-link);
        font-size: var(--dees-font-caption2-size, 10px);
        font-weight: 600;
      }

      .resourceAttachment {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: 2px;
      }

      /* Past two names the row keeps one line and the title carries the full list. */
      .resourceAttachmentOverflow {
        flex: 0 0 auto;
      }

      .resourceAttachments dees-icon {
        flex: 0 0 auto;
      }

      /*
       * A drop on the centre of a row attaches to it. The ring is the whole affordance: no gap
       * opens, because nothing is being inserted.
       */
      .item.attachTarget,
      .resourceRow.attachTarget {
        outline: 2px solid var(--dees-color-accent-primary);
        outline-offset: -1px;
        background: var(--dees-color-nav-selected);
      }

      /* Absolutely positioned so appearing mid-drag cannot reflow the row under the pointer. */
      .attachBadge {
        position: absolute;
        top: 50%;
        right: 10px;
        display: grid;
        place-items: center;
        width: 20px;
        height: 20px;
        transform: translateY(-50%);
        border-radius: var(--dees-radius-full);
        background: var(--dees-color-accent-primary);
        color: var(--dees-color-bg-primary);
        pointer-events: none;
      }

      .resourceAttachmentName {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .resourceActions {
        display: grid;
        place-items: center;
        min-width: 34px;
        min-height: 34px;
        margin: 2px;
        padding: 0;
        border-radius: var(--dees-radius-md);
        color: var(--dees-color-text-muted);
        cursor: pointer;
      }

      .resourceDragHandle:focus-visible,
      .resourcePrimary:focus-visible,
      .resourceActions:focus-visible {
        z-index: 1;
        outline: 2px solid var(--dees-color-accent-primary);
        outline-offset: -3px;
        border-radius: var(--dees-radius-md);
      }

      .visuallyHidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .dropPlaceholder,
      .emptyDropZone {
        display: grid;
        place-items: center;
        overflow: hidden;
        border: 1px dashed color-mix(in srgb, var(--dees-color-accent-primary) 68%, transparent);
        border-radius: var(--dees-radius-xl);
        background: color-mix(in srgb, var(--dees-color-accent-primary) 8%, transparent);
        color: var(--dees-color-accent-primary);
        box-sizing: border-box;
      }

      /* The placeholder matches the dragged row exactly; only the empty zone has a floor. */
      .emptyDropZone {
        min-height: 46px;
      }

      .dropPlaceholder::after,
      .emptyDropZone::after {
        content: 'Drop here';
        font-size: var(--dees-font-caption2-size, 10px);
        font-weight: 650;
        letter-spacing: 0.02em;
      }

      .emptyNote {
        padding: var(--dees-spacing-lg);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-control-size-sm, 12px);
        text-align: center;
      }

      .groupHeader {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        width: 100%;
        margin: var(--dees-spacing-md) 0 7px;
        padding: var(--dees-spacing-xs) 6px;
        border: 0;
        border-radius: var(--dees-radius-md);
        background: transparent;
        color: var(--dees-color-text-secondary);
        cursor: pointer;
        font-family: inherit;
        font-size: var(--dees-font-caption1-size, 11px);
        font-weight: 600;
        text-align: left;
      }

      .groupHeader .chevron {
        transition: transform var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .groupHeader.collapsed .chevron {
        transform: rotate(-90deg);
      }

      .groupHeader .groupCount {
        margin-left: auto;
        font-weight: 500;
        text-transform: none;
        letter-spacing: normal;
        color: var(--dees-color-text-muted);
      }

      .groupHeader.dropTarget {
        outline: 1px dashed var(--dees-color-accent-primary);
        outline-offset: -1px;
      }

      .groupAttention {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin-left: auto;
        color: var(--dees-color-text-warning);
        font-size: var(--dees-font-caption2-size, 10px);
      }

      .groupAttention + .groupCount { margin-left: 10px; }

      .newGroupButton {
        appearance: none;
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: var(--dees-control-height-lg);
        height: var(--dees-control-height-lg);
        padding: 0;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-control-bg);
        color: var(--dees-color-text-secondary);
        cursor: pointer;
        font-family: inherit;
      }

      .newGroupButton:focus-visible {
        outline: 2px solid var(--dees-color-accent-primary);
        outline-offset: 2px;
      }

      @media (hover: hover) {
        .item:hover {
          --card-inner-background: var(--dees-color-hover);
        }

        .item.selected:hover {
          --card-inner-background: var(--dees-color-nav-selected);
        }

        .sessionActions:hover,
        .expandToggle:hover {
          background: color-mix(in srgb, var(--card-accent) 11%, transparent);
          color: var(--dees-color-text-primary);
        }

        .resourceActions:hover {
          background: var(--dees-color-hover);
          color: var(--dees-color-text-primary);
        }

        .groupHeader:hover {
          background: var(--dees-color-hover);
        }

        .newGroupButton:hover {
          background: var(--dees-color-hover);
          color: var(--dees-color-text-primary);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .item,
        .itemInner,
        .item.active .itemHeader .stateDot,
        .scrollShadow,
        .expandToggle dees-icon {
          animation: none;
          transition: none;
        }

      }

      @media (pointer: coarse) {
        .searchRow input { height: 44px; }
        .newGroupButton { width: 44px; height: 44px; }
        .itemHeader { grid-template-columns: minmax(0, 1fr) 44px 47px; }
        .itemHeader.draggable { grid-template-columns: 44px minmax(0, 1fr) 44px 47px; }
        .dragHandle,
        .sessionActions,
        .expandToggle { min-width: 44px; min-height: 44px; }
        .sessionActions { margin: 0; }
        .expandToggle { margin: 0 3px 0 0; }

        .resourceRow {
          grid-template-columns: minmax(0, 1fr) 44px;
        }

        .resourceRow.draggable {
          grid-template-columns: 44px minmax(0, 1fr) 44px;
        }

        .resourceDragHandle,
        .resourceActions {
          min-width: 44px;
          min-height: 44px;
          margin: 0;
        }

        .resourcePrimary {
          min-height: 44px;
        }
      }

      @media (max-width: 280px) {
        .resourcePreview {
          display: none;
        }
      }
    `,
  ];

  private get resourcesById(): Map<string, IHarnessResourceMeta> {
    const result = new Map<string, IHarnessResourceMeta>();
    for (const resource of this.resources) {
      if (!resource.id || !resource.kind || !resource.title || result.has(resource.id)) continue;
      result.set(resource.id, resource);
    }
    return result;
  }

  /**
   * First valid record wins; unknown records, self-references and duplicates
   * remain untouched but are not projected. Keyed by resource id.
   */
  private get acceptedResourceAssociations(): Map<string, IHarnessSessionListItemRef[]> {
    const resourcesById = this.resourcesById;
    const itemsByKey = this.itemsByKey;
    const result = new Map<string, IHarnessSessionListItemRef[]>();
    for (const association of this.resourceAssociations) {
      if (!association || !resourcesById.has(association.resourceId)) continue;
      const targets = result.get(association.resourceId) ?? [];
      for (const target of association.targets ?? []) {
        if (
          !target
          || !itemsByKey.has(itemKey(target))
          || sameItem(target, { kind: 'resource', id: association.resourceId })
          || targets.some((candidate) => sameItem(candidate, target))
        ) continue;
        targets.push(itemRef(target));
      }
      result.set(association.resourceId, targets);
    }
    return result;
  }

  /** The controlled attachments of one resource, in declaration order; empty when unattached. */
  private resourceTargets(resourceIdArg: string): IHarnessSessionListItemRef[] {
    return this.acceptedResourceAssociations.get(resourceIdArg) ?? [];
  }

  private get acceptedResources(): IHarnessResourceMeta[] {
    return [...this.resourcesById.values()];
  }

  /** Sessions and resources as one pool of orderable items, before grouping and search. */
  private get acceptedItems(): TListItem[] {
    const result: TListItem[] = [];
    const seenSessionIds = new Set<string>();
    for (const session of this.sessions) {
      if (!session.id || seenSessionIds.has(session.id)) continue;
      seenSessionIds.add(session.id);
      result.push({
        kind: 'session',
        key: `session:${session.id}`,
        id: session.id,
        title: session.title || session.id,
        sortable: session.draggable !== false,
        timestamp: session.updatedAt ?? session.createdAt ?? 0,
        session,
      });
    }
    for (const resource of this.resourcesById.values()) {
      result.push({
        kind: 'resource',
        key: `resource:${resource.id}`,
        id: resource.id,
        title: resource.title || resource.id,
        sortable: resource.draggable !== false,
        timestamp: resource.updatedAt ?? resource.createdAt ?? 0,
        resource,
      });
    }
    return result;
  }

  private get itemsByKey(): Map<string, TListItem> {
    return new Map(this.acceptedItems.map((item) => [item.key, item]));
  }

  /** Groups only organize the list while grouping is enabled. */
  private get effectiveGroups(): IHarnessSessionGroup[] {
    return this.enableGrouping ? this.groups : [];
  }

  private get groupedItemKeys(): Set<string> {
    const keys = new Set<string>();
    for (const group of this.effectiveGroups) {
      for (const ref of group.itemIds) keys.add(itemKey(ref));
    }
    return keys;
  }

  private itemsForGroup(groupIdArg: string | null): TListItem[] {
    if (groupIdArg === null) return this.ungroupedItems;
    const group = this.effectiveGroups.find((candidate) => candidate.id === groupIdArg);
    if (!group) return [];
    const byKey = this.itemsByKey;
    const seen = new Set<string>();
    const result: TListItem[] = [];
    for (const ref of group.itemIds) {
      const key = itemKey(ref);
      const item = byKey.get(key);
      if (!item || seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
    return result;
  }

  /** Pinned entries first, then explicitly ordered items, then unknown items by recency. */
  private get ungroupedItems(): TListItem[] {
    const grouped = this.groupedItemKeys;
    const available = this.acceptedItems.filter((item) => !grouped.has(item.key));
    const pinned = available.filter((item) => !item.sortable);
    const sortableByKey = new Map(
      available.filter((item) => item.sortable).map((item) => [item.key, item]),
    );
    const ordered: TListItem[] = [];
    for (const ref of this.ungroupedItemIds) {
      const item = sortableByKey.get(itemKey(ref));
      if (!item) continue;
      ordered.push(item);
      sortableByKey.delete(item.key);
    }
    const unknown = [...sortableByKey.values()].sort(
      (left, right) => right.timestamp - left.timestamp,
    );
    return [...pinned, ...ordered, ...unknown];
  }

  private resourceMatchesSearch(resourceArg: IHarnessResourceMeta, queryArg: string): boolean {
    return [resourceArg.id, resourceArg.kind, resourceArg.title, resourceArg.preview,
      resourceArg.projectLabel]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(queryArg);
  }

  private sessionMatchesSearch(sessionArg: IHarnessSessionMeta, queryArg: string): boolean {
    return [sessionArg.title, sessionArg.preview, sessionArg.id, sessionArg.harness?.id,
      sessionArg.harness?.name, sessionArg.projectLabel,
      this.sessionStatusLabel(sessionArg), this.sessionStateLabel(sessionArg.state ?? 'normal')]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(queryArg);
  }

  private itemMatchesSearch(itemArg: TListItem, queryArg: string): boolean {
    return itemArg.kind === 'session'
      ? this.sessionMatchesSearch(itemArg.session, queryArg)
      : this.resourceMatchesSearch(itemArg.resource, queryArg);
  }

  /** Search flattens the list: sessions and resources match as peers, newest first. */
  private get searchItems(): TListItem[] {
    const query = this.search.trim().toLowerCase();
    const sorted = [...this.acceptedItems].sort(
      (left, right) => right.timestamp - left.timestamp,
    );
    if (!query) return sorted;
    return sorted.filter((item) => this.itemMatchesSearch(item, query));
  }

  private get searchActive(): boolean {
    return this.search.trim().length > 0;
  }

  private sortableItems(itemsArg: TListItem[], excludeDraggingArg = true): TListItem[] {
    return itemsArg.filter((item) => (
      item.sortable
      && (!excludeDraggingArg || !sameItem(this.draggingItem, item))
    ));
  }

  private groupIdForItem(refArg: IHarnessSessionListItemRef): string | null {
    return this.effectiveGroups.find(
      (group) => group.itemIds.some((candidate) => sameItem(candidate, refArg)),
    )?.id ?? null;
  }

  private rowElementFor(refArg: IHarnessSessionListItemRef): HTMLElement | undefined {
    return Array.from(
      this.shadowRoot?.querySelectorAll<HTMLElement>('[data-item-kind][data-item-id]') ?? [],
    ).find((candidate) => (
      candidate.dataset.itemKind === refArg.kind && candidate.dataset.itemId === refArg.id
    ));
  }

  public focusSearch(): void {
    this.shadowRoot?.querySelector<HTMLInputElement>('.searchRow input')?.focus();
  }

  /**
   * Requests one association change. With no explicit mode the list infers it from the controlled
   * set: an unattached resource attaches, a singly attached one moves, and a resource with several
   * attachments is refused — only the host knows which of them the user meant, so it must pass a
   * mode (and a `fromTarget` for `move` and `detach`).
   */
  public requestResourceAssociation(
    resourceIdArg: string,
    toTargetArg: IHarnessSessionListItemRef | null,
    sourceArg: THarnessResourceAssociationInteractionSource = 'action',
    modeArg?: THarnessResourceAssociateMode,
    fromTargetArg?: IHarnessSessionListItemRef | null,
  ): boolean {
    const resource = this.resourcesById.get(resourceIdArg);
    if (!resource) return false;
    const currentTargets = this.resourceTargets(resourceIdArg);
    const toTarget = toTargetArg === null || toTargetArg === undefined ? null : itemRef(toTargetArg);
    if (
      toTarget !== null
      && (
        !this.itemsByKey.has(itemKey(toTarget))
        || sameItem(toTarget, { kind: 'resource', id: resourceIdArg })
        || !this.isResourceTargetEligible(resourceIdArg, toTarget)
      )
    ) return false;

    const mode = modeArg ?? (
      toTarget === null
        ? 'detach'
        : currentTargets.length === 0
          ? 'attach'
          : currentTargets.length === 1
            ? 'move'
            : undefined
    );
    // An ambiguous request is refused rather than guessed at.
    if (!mode) return false;

    const explicitFrom = fromTargetArg === null || fromTargetArg === undefined
      ? undefined
      : itemRef(fromTargetArg);
    const fromTarget = explicitFrom
      ?? (currentTargets.length === 1 ? currentTargets[0]! : undefined);

    if (mode === 'attach' || mode === 'attach-additional') {
      if (toTarget === null) return false;
      if (currentTargets.some((candidate) => sameItem(candidate, toTarget))) return false;
      if (mode === 'attach' && currentTargets.length > 0) return false;
      if (mode === 'attach-additional' && currentTargets.length === 0) return false;
    }
    if (mode === 'move' || mode === 'detach') {
      if (!fromTarget) return false;
      if (!currentTargets.some((candidate) => sameItem(candidate, fromTarget))) return false;
      if (mode === 'move') {
        if (toTarget === null || sameItem(fromTarget, toTarget)) return false;
        if (currentTargets.some((candidate) => sameItem(candidate, toTarget))) return false;
      }
      if (mode === 'detach' && toTarget !== null) return false;
    }

    const detail = {
      mode,
      resource,
      fromTarget: mode === 'attach' || mode === 'attach-additional' ? null : fromTarget!,
      toTarget: mode === 'detach' ? null : toTarget!,
      source: sourceArg,
    } as THarnessResourceAssociateDetail;

    const expectedTargets = mode === 'attach' || mode === 'attach-additional'
      ? [...currentTargets, toTarget!]
      : mode === 'detach'
        ? currentTargets.filter((candidate) => !sameItem(candidate, fromTarget!))
        : currentTargets.map((candidate) => (sameItem(candidate, fromTarget!) ? toTarget! : candidate));

    this.pendingResourceAssociationRequest = {
      resourceId: resource.id,
      resourceTitle: resource.title || resource.id,
      fromTarget: detail.fromTarget,
      toTarget: detail.toTarget,
      mode,
      expectedTargets,
    };
    this.moveAnnouncement = this.resourceAssociationAnnouncement(detail, false);
    this.dispatchEvent(
      new CustomEvent('harness-resource-associate', {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
    return true;
  }

  public updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    this.observeScrollGeometry();
    this.syncScrollShadowState();
    this.restoreKeyboardMoveFocus();
    if (
      this.pointerDragGesture
      && (!this.pointerDragGesture.source.isConnected || !this.pointerDragGesture.handle.isConnected)
    ) {
      this.finishPointerDrag(false);
    }
    if (changedProperties.has('sessions')) {
      const knownIds = new Set(this.sessions.map((session) => session.id));
      const nextExpanded = new Set(
        [...this.expandedSessionIds].filter((sessionId) => knownIds.has(sessionId)),
      );
      if (
        nextExpanded.size !== this.expandedSessionIds.size
        || [...nextExpanded].some((sessionId) => !this.expandedSessionIds.has(sessionId))
      ) {
        this.expandedSessionIds = nextExpanded;
      }
    }
    if (changedProperties.has('resources') || changedProperties.has('resourceAssociations')) {
      if (
        this.resourceContextResourceId
        && !this.resourcesById.has(this.resourceContextResourceId)
      ) this.closeResourceContext(this.resourceContextGeneration);
      this.reconcilePendingResourceAssociation();
    }
    if (
      changedProperties.has('sessions')
      || changedProperties.has('resources')
      || changedProperties.has('search')
    ) {
      // A drag is positional only: it survives association and eligibility
      // updates, but never outlives its own row.
      const gesture = this.pointerDragGesture;
      if (gesture && (this.searchActive || !this.itemsByKey.has(itemKey(gesture.item)))) {
        this.finishPointerDrag(false);
      }
    }
  }

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    await this.updateComplete;
    if (!this.isConnected) return;
    this.observeScrollGeometry();
    this.syncScrollShadowState();
  }

  public async disconnectedCallback(): Promise<void> {
    this.finishPointerDrag(false);
    this.clearResourceContextState();
    this.scrollResizeObserver?.disconnect();
    this.scrollResizeObserver = undefined;
    this.observedScrollContainer = undefined;
    this.observedScrollContent = undefined;
    await super.disconnectedCallback();
  }

  public render(): TemplateResult {
    return html`
      <div class="searchRow">
        <div class="searchField">
          <dees-icon class="searchIcon" icon="lucide:Search" iconSize="13"></dees-icon>
          <input
            placeholder=${this.acceptedResources.length
              ? 'Search conversations and resources'
              : 'Search conversations'}
            aria-label=${this.acceptedResources.length
              ? 'Search conversations and resources'
              : 'Search conversations'}
            .value=${this.search}
            @input=${(event: Event) => {
              this.search = (event.currentTarget as HTMLInputElement).value;
            }}
          />
        </div>
        ${this.enableGrouping ? html`
          <button
            class="newGroupButton"
            type="button"
            aria-label="New group"
            title="New group"
            @click=${() => {
              this.dispatchEvent(new CustomEvent('harness-group-create-request', {
                bubbles: true,
                composed: true,
              }));
            }}
          >
            <dees-icon icon="lucide:FolderPlus" iconSize="14" aria-hidden="true"></dees-icon>
          </button>
        ` : ''}
      </div>
      <div class="listFrame">
        <div class="scrollShadow top ${this.topScrollShadowVisible ? 'visible' : ''}"></div>
        <div
          class="list ${this.draggingItem ? 'dragActive' : ''}"
          @scroll=${this.syncScrollShadowState}
        >
          <div class="listContent">
            ${this.loading
              ? html`<div class="emptyNote">Loading conversations…</div>`
              : this.searchActive
                ? this.renderSearchList()
                : this.renderOrderedList()}
          </div>
        </div>
        <div class="scrollShadow bottom ${this.bottomScrollShadowVisible ? 'visible' : ''}"></div>
      </div>
      <div class="visuallyHidden" aria-live="polite">${this.moveAnnouncement}</div>
    `;
  }

  private renderEmptyNote(): TemplateResult {
    return html`<div class="emptyNote">
      ${this.acceptedResources.length
        ? this.sessions.length
          ? 'No conversations or resources match this search.'
          : 'No resources match this search.'
        : this.sessions.length
          ? 'No conversations match this search.'
          : this.emptyText}
    </div>`;
  }

  private renderSearchList(): TemplateResult {
    const items = this.searchItems;
    if (!items.length) return this.renderEmptyNote();
    return this.renderSection(items, null, false);
  }

  private renderOrderedList(): TemplateResult {
    const groups = this.effectiveGroups;
    const ungrouped = this.ungroupedItems;
    if (!this.acceptedItems.length && !groups.length) {
      return html`<div class="emptyNote">${this.emptyText}</div>`;
    }
    return html`
      ${groups.map((group) => this.renderGroup(group))}
      ${groups.length && (ungrouped.length || this.draggingItem)
        ? html`
            <button
              class="groupHeader"
              data-drop-group-id=""
              data-drop-index="0"
              data-drop-kind="slot"
              type="button"
              tabindex="-1"
            >
              <span>Ungrouped</span>
              <span class="groupCount">${ungrouped.length}</span>
            </button>
          `
        : ''}
      ${this.renderSection(ungrouped, null, true)}
    `;
  }

  private renderGroup(groupArg: IHarnessSessionGroup): TemplateResult {
    const members = this.itemsForGroup(groupArg.id);
    const collapsed = this.collapsedGroupIds.has(groupArg.id);
    const attentionCount = members.filter((item) => (
      item.kind === 'session' && (item.session.state === 'attention' || item.session.state === 'error')
    )).length;
    const appendIndex = this.sortableItems(members).length;
    const isHeaderDrop = this.dropSlot?.groupId === groupArg.id
      && this.dropSlot.index === appendIndex
      && collapsed;
    return html`
      <button
        class="groupHeader ${collapsed ? 'collapsed' : ''} ${isHeaderDrop ? 'dropTarget' : ''}"
        data-drop-group-id=${groupArg.id}
        data-drop-index=${appendIndex}
        data-drop-kind="slot"
        type="button"
        @click=${() => this.toggleGroupCollapsed(groupArg.id)}
        @contextmenu=${(event: MouseEvent) => {
          event.preventDefault();
          const detail: IHarnessGroupContextDetail = {
            group: groupArg,
            clientX: event.clientX,
            clientY: event.clientY,
            originalEvent: event,
          };
          this.dispatchEvent(
            new CustomEvent('harness-group-context', { detail, bubbles: true, composed: true }),
          );
        }}
      >
        <dees-icon class="chevron" icon="lucide:ChevronDown" iconSize="12"></dees-icon>
        <span>${groupArg.name}</span>
        ${attentionCount ? html`<span class="groupAttention" aria-label=${`${attentionCount} need attention`}>
          <dees-icon aria-hidden="true" icon="lucide:CircleAlert" iconSize="11"></dees-icon>${attentionCount}
        </span>` : ''}
        <span class="groupCount">${members.length}</span>
      </button>
      ${collapsed ? '' : this.renderSection(members, groupArg.id, true)}
    `;
  }

  private renderSection(
    itemsArg: TListItem[],
    groupIdArg: string | null,
    sortableArg: boolean,
  ): TemplateResult {
    const slot = sortableArg ? this.dropSlot : undefined;
    const entries: TSectionRenderEntry[] = [];
    let logicalIndex = 0;
    for (const item of itemsArg) {
      const isDragging = sameItem(this.draggingItem, item);
      const sortable = sortableArg && item.sortable;
      if (
        slot
        && slot.groupId === groupIdArg
        && slot.index === logicalIndex
        && !isDragging
        && sortable
      ) {
        entries.push({ type: 'placeholder', key: `placeholder:${groupIdArg ?? 'ungrouped'}` });
      }
      entries.push({
        type: 'item',
        key: item.key,
        item,
        logicalIndex: sortable ? logicalIndex : -1,
      });
      if (sortable && !isDragging) logicalIndex += 1;
    }
    if (slot && slot.groupId === groupIdArg && slot.index === logicalIndex) {
      entries.push({ type: 'placeholder', key: `placeholder:${groupIdArg ?? 'ungrouped'}` });
    }
    const hasPlaceholder = entries.some((entry) => entry.type === 'placeholder');
    // Resolve the controlled associations once per section, not once per row.
    const associations = this.acceptedResourceAssociations;
    return html`
      <div class="section" data-group-id=${groupIdArg ?? ''}>
        ${repeat(
          entries,
          (entry) => entry.key,
          (entry) => entry.type === 'placeholder'
            ? this.renderDropPlaceholder(groupIdArg, slot?.index ?? 0)
            : entry.item.kind === 'session'
              ? this.renderSessionItem(entry.item.session, groupIdArg, entry.logicalIndex)
              : this.renderResourceItem(
                  entry.item.resource,
                  groupIdArg,
                  entry.logicalIndex,
                  associations.get(entry.item.id) ?? [],
                ),
        )}
        ${this.draggingItem && sortableArg && !hasPlaceholder && this.sortableItems(itemsArg).length === 0
          ? html`
              <div
                class="emptyDropZone"
                data-drop-group-id=${groupIdArg ?? ''}
                data-drop-index="0"
                data-drop-kind="slot"
              ></div>
            `
          : ''}
      </div>
    `;
  }

  private renderDropPlaceholder(groupId: string | null, index: number): TemplateResult {
    return html`
      <div
        class="dropPlaceholder"
        data-drop-group-id=${groupId ?? ''}
        data-drop-index=${index}
        data-drop-kind="slot"
        style=${`height: ${Math.max(24, this.draggedItemHeight)}px`}
      ></div>
    `;
  }

  /** One grip for both kinds; only its size and extra class differ. */
  private renderDragHandle(
    refArg: IHarnessSessionListItemRef,
    titleArg: string,
    groupIdArg: string | null,
    logicalIndexArg: number,
  ): TemplateResult {
    const isResource = refArg.kind === 'resource';
    return html`
      <button
        class="dragHandle ${isResource ? 'resourceDragHandle' : ''}"
        type="button"
        title="Drag to reorder; use arrow, Home, or End keys for keyboard reordering"
        aria-label=${`Reorder ${titleArg}`}
        aria-keyshortcuts="ArrowUp ArrowDown Home End"
        @pointerdown=${(event: PointerEvent) => {
          this.handlePointerDown(event, refArg, groupIdArg, logicalIndexArg);
        }}
        @pointermove=${this.handlePointerMove}
        @pointerup=${this.handlePointerUp}
        @pointercancel=${this.handlePointerCancel}
        @lostpointercapture=${this.handleLostPointerCapture}
        @keydown=${(event: KeyboardEvent) => {
          this.handleKeyboardMove(event, refArg, titleArg, groupIdArg);
        }}
      >
        <dees-icon icon="lucide:GripVertical" iconSize=${isResource ? '13' : '14'}></dees-icon>
      </button>
    `;
  }

  private renderSessionItem(
    session: IHarnessSessionMeta,
    groupId: string | null,
    index: number,
  ): TemplateResult {
    const draggable = index >= 0;
    const expanded = this.expandedSessionIds.has(session.id);
    const state = session.state ?? 'normal';
    const statusLabel = this.sessionStatusLabel(session);
    const active = (state === 'normal' || state === 'working') && (session.working ?? state === 'working');
    const detailsId = `session-details-${session.id}`;
    const dropIndex = index < 0 ? 0 : index;
    const title = session.title || session.id;
    const projectLabel = session.projectLabel?.trim() ?? '';
    return html`
      <article
        class="item state-${state} ${active ? 'active' : ''} ${expanded ? 'expanded' : ''} ${
          session.id === this.selectedSessionId ? 'selected' : ''
        } ${
          sameItem(this.draggingItem, { kind: 'session', id: session.id }) ? 'dragging' : ''
        } ${
          sameItem(this.dropAttachTarget ?? null, { kind: 'session', id: session.id })
            ? 'attachTarget'
            : ''
        }"
        data-session-id=${session.id}
        data-item-kind="session"
        data-item-id=${session.id}
        data-drop-group-id=${groupId ?? ''}
        data-drop-index=${dropIndex}
        data-drop-kind=${index < 0 ? 'slot' : 'card'}
        data-attach-target=${
          sameItem(this.dropAttachTarget ?? null, { kind: 'session', id: session.id }) ? 'true' : ''
        }
        @dblclick=${(event: MouseEvent) => {
          if (this.eventTargetsExpandToggle(event) || this.eventTargetsDragHandle(event)
            || this.eventTargetsSessionActions(event)) return;
          this.emitSession('harness-session-open', session);
        }}
        @contextmenu=${(event: MouseEvent) => {
          if (this.eventTargetsExpandToggle(event)) return;
          event.preventDefault();
          event.stopPropagation();
          const detail: IHarnessSessionContextDetail = {
            session,
            clientX: event.clientX,
            clientY: event.clientY,
            originalEvent: event,
          };
          this.dispatchEvent(
            new CustomEvent('harness-session-context', { detail, bubbles: true, composed: true }),
          );
        }}
      >
        ${sameItem(this.dropAttachTarget ?? null, { kind: 'session', id: session.id })
          ? html`<span class="attachBadge" aria-hidden="true"><dees-icon icon="lucide:Link" iconSize="12"></dees-icon></span>`
          : ''}
        <div class="itemInner">
          <div class="itemHeader ${draggable ? 'draggable' : ''}">
            ${draggable
              ? this.renderDragHandle({ kind: 'session', id: session.id }, title, groupId, index)
              : ''}
            <button
              class="itemPrimary"
              type="button"
              aria-keyshortcuts="ContextMenu Shift+F10"
              aria-current=${session.id === this.selectedSessionId ? 'true' : 'false'}
              @click=${() => this.emitSession('harness-session-select', session)}
              @keydown=${(event: KeyboardEvent) => {
                if (this.handleSessionContextKey(event)) return;
                if (event.key !== 'Enter') return;
                event.preventDefault();
                this.emitSession('harness-session-open', session);
              }}
            >
              <span class="stateDot" aria-hidden="true">
                <dees-icon icon=${this.sessionStateIcon(state)} iconSize="14"></dees-icon>
              </span>
              <span class="itemCopy">
                <span class="itemTitle">
                  ${session.icon
                    ? html`<dees-icon class="itemIcon" icon=${session.icon} iconSize="13"></dees-icon>`
                    : ''}
                  <span class="titleText">${title}</span>
                </span>
                <span class="itemStatus" title=${statusLabel}>${statusLabel}</span>
                <span class="itemIdentity">
                  ${session.harness ? html`
                    ${session.harness.icon ? html`<dees-icon aria-hidden="true" icon=${session.harness.icon} iconSize="11"></dees-icon>` : ''}
                    <span class="harnessName" title=${session.harness.name}>${session.harness.name}</span>
                    <span aria-hidden="true">·</span>
                  ` : ''}
                  ${projectLabel ? html`
                    <span class="projectName" title=${projectLabel}>${projectLabel}</span>
                    <span aria-hidden="true">·</span>
                  ` : ''}
                  <span class="itemTime">${harnessFormatRelativeTime(session.updatedAt ?? session.createdAt)}</span>
                </span>
              </span>
            </button>
            <button
              class="sessionActions"
              type="button"
              title="Actions"
              aria-label=${`Actions for ${title}`}
              aria-haspopup="menu"
              aria-keyshortcuts="ContextMenu Shift+F10"
              @click=${this.invokeSessionContext}
              @keydown=${this.handleSessionContextKey}
            >
              <dees-icon icon="lucide:Ellipsis" iconSize="15"></dees-icon>
            </button>
            <button
              class="expandToggle"
              type="button"
              aria-expanded=${expanded ? 'true' : 'false'}
              aria-controls=${detailsId}
              title=${expanded ? 'Collapse conversation card' : 'Expand conversation card'}
              @click=${(event: MouseEvent) => {
                event.stopPropagation();
                this.toggleSessionExpanded(session.id);
              }}
            >
              <dees-icon icon="lucide:ChevronDown" iconSize="15"></dees-icon>
            </button>
          </div>
          ${expanded
            ? html`
                <div class="itemDetails" id=${detailsId}>
                  ${session.preview ? html`<div class="itemPreview">${session.preview}</div>` : ''}
                  <div class="itemMeta">
                    ${session.messageCount ? `${session.messageCount} messages · ` : ''}${session.id}
                  </div>
                </div>
              `
            : ''}
        </div>
      </article>
    `;
  }

  private renderResourceItem(
    resourceArg: IHarnessResourceMeta,
    groupIdArg: string | null,
    indexArg: number,
    attachedTargets: IHarnessSessionListItemRef[],
  ): TemplateResult {
    const selected = resourceArg.id === this.selectedResourceId;
    const draggable = indexArg >= 0;
    const dragging = sameItem(this.draggingItem, { kind: 'resource', id: resourceArg.id });
    const contextOpen = resourceArg.id === this.resourceContextResourceId;
    const attachedTitles = attachedTargets.map((target) => this.itemTitleFor(target));
    // Two names read at a glance; beyond that a count keeps the row one line, with every name
    // still reachable through the title.
    const visibleAttachments = attachedTargets.slice(0, 2);
    const overflowAttachments = attachedTargets.length - visibleAttachments.length;
    const title = resourceArg.title || resourceArg.id;
    const projectLabel = resourceArg.projectLabel?.trim() ?? '';
    const dropIndex = indexArg < 0 ? 0 : indexArg;
    return html`
      <article
        class="resourceRow ${selected ? 'selected' : ''} ${draggable ? 'draggable' : ''} ${
          dragging ? 'dragging' : ''
        } ${
          sameItem(this.dropAttachTarget ?? null, { kind: 'resource', id: resourceArg.id })
            ? 'attachTarget'
            : ''
        }"
        data-resource-id=${resourceArg.id}
        data-item-kind="resource"
        data-item-id=${resourceArg.id}
        data-resource-target=${attachedTargets.map((target) => `${target.kind}:${target.id}`).join(' ')}
        data-resource-target-count=${attachedTargets.length}
        data-drop-group-id=${groupIdArg ?? ''}
        data-drop-index=${dropIndex}
        data-drop-kind=${indexArg < 0 ? 'slot' : 'card'}
        data-attach-target=${
          sameItem(this.dropAttachTarget ?? null, { kind: 'resource', id: resourceArg.id })
            ? 'true'
            : ''
        }
        @dblclick=${(event: MouseEvent) => {
          event.stopPropagation();
          if (this.eventTargetsResourceControl(event) || this.eventTargetsDragHandle(event)) return;
          this.emitResource('harness-resource-open', resourceArg, attachedTargets);
        }}
        @contextmenu=${(event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          const focusTarget = (event.composedPath().find((target) => (
            target instanceof HTMLElement
            && target.matches('button:not(:disabled)')
          )) as HTMLElement | undefined)
            ?? (event.currentTarget as HTMLElement).querySelector<HTMLElement>('.resourcePrimary')
            ?? event.currentTarget as HTMLElement;
          this.emitResourceContext(event, resourceArg, attachedTargets, 'pointer', focusTarget);
        }}
      >
        ${sameItem(this.dropAttachTarget ?? null, { kind: 'resource', id: resourceArg.id })
          ? html`<span class="attachBadge" aria-hidden="true"><dees-icon icon="lucide:Link" iconSize="12"></dees-icon></span>`
          : ''}
        ${draggable
          ? this.renderDragHandle(
              { kind: 'resource', id: resourceArg.id },
              title,
              groupIdArg,
              indexArg,
            )
          : ''}
        <button
          class="resourcePrimary"
          type="button"
          aria-current=${selected ? 'true' : 'false'}
          @click=${() => this.emitResource('harness-resource-select', resourceArg, attachedTargets)}
          @keydown=${(event: KeyboardEvent) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.emitResource('harness-resource-open', resourceArg, attachedTargets);
              return;
            }
            this.handleResourceContextKey(event, resourceArg, attachedTargets);
          }}
        >
          <dees-icon
            class="resourceIcon"
            icon=${resourceArg.icon || 'lucide:Box'}
            iconSize="14"
          ></dees-icon>
          <span class="resourceCopy">
            <span class="resourceTitle">${title}</span>
            ${resourceArg.preview ? html`<span class="resourcePreview">${resourceArg.preview}</span>` : ''}
            <span class="resourceMeta">
              ${resourceArg.kind}${projectLabel ? ` · ${projectLabel}` : ''}${
                resourceArg.state ? ` · ${resourceArg.state}` : ''
              }${
                resourceArg.updatedAt || resourceArg.createdAt
                  ? ` · ${harnessFormatRelativeTime(resourceArg.updatedAt ?? resourceArg.createdAt)}`
                  : ''
              }
            </span>
            ${attachedTargets.length
              ? html`
                  <span class="resourceAttachments" title=${`Attached to ${attachedTitles.join(', ')}`}>
                    <dees-icon aria-hidden="true" icon="lucide:Link" iconSize="10"></dees-icon>
                    <span class="visuallyHidden">Attached to </span>
                    ${repeat(
                      visibleAttachments,
                      (target) => itemKey(target),
                      (target, index) => html`
                        <span class="resourceAttachment">
                          <span class="resourceAttachmentName">${this.itemTitleFor(target)}</span>
                          ${index < visibleAttachments.length - 1 || overflowAttachments > 0
                            ? html`<span aria-hidden="true">,</span>`
                            : ''}
                        </span>
                      `,
                    )}
                    ${overflowAttachments > 0
                      ? html`<span class="resourceAttachmentOverflow">+${overflowAttachments}</span>`
                      : ''}
                  </span>
                `
              : ''}
          </span>
        </button>
        <button
          class="resourceActions"
          type="button"
          title="Resource actions"
          aria-label=${`Actions for ${title}`}
          aria-haspopup="menu"
          aria-expanded=${contextOpen ? 'true' : 'false'}
          aria-keyshortcuts="ContextMenu Shift+F10"
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            this.emitResourceContext(
              event,
              resourceArg,
              attachedTargets,
              'action',
              event.currentTarget as HTMLElement,
            );
          }}
          @keydown=${(event: KeyboardEvent) => {
            this.handleResourceContextKey(event, resourceArg, attachedTargets);
          }}
        >
          <dees-icon icon="lucide:Ellipsis" iconSize="15"></dees-icon>
        </button>
      </article>
    `;
  }

  private invokeSessionContext(event: MouseEvent | KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const control = event.currentTarget as HTMLElement;
    const rect = control.getBoundingClientRect();
    // Use the existing context-menu path, including its composed dialog owner.
    control.dispatchEvent(new MouseEvent('contextmenu', {
      bubbles: true, composed: true, cancelable: true,
      clientX: rect.left + rect.width / 2, clientY: rect.bottom,
    }));
  }

  private handleSessionContextKey(event: KeyboardEvent): boolean {
    if (event.key !== 'ContextMenu' && !(event.key === 'F10' && event.shiftKey)) return false;
    this.invokeSessionContext(event);
    return true;
  }

  private eventTargetsSessionActions(event: Event): boolean {
    return event.composedPath().some(
      (target) => target instanceof HTMLElement && target.classList.contains('sessionActions'),
    );
  }

  private eventTargetsExpandToggle(event: Event): boolean {
    return event.composedPath().some(
      (target) => target instanceof HTMLElement && target.classList.contains('expandToggle'),
    );
  }

  private eventTargetsDragHandle(event: Event): boolean {
    return event.composedPath().some(
      (target) => target instanceof HTMLElement && target.classList.contains('dragHandle'),
    );
  }

  private eventTargetsResourceControl(event: Event): boolean {
    return event.composedPath().some(
      (target) => target instanceof HTMLElement && target.classList.contains('resourceActions'),
    );
  }

  private sessionStateLabel(state: NonNullable<IHarnessSessionMeta['state']>): string {
    switch (state) {
      case 'working': return 'Working';
      case 'external': return 'Running elsewhere';
      case 'finished': return 'Finished';
      case 'attention': return 'Needs attention';
      case 'error': return 'Error';
      default: return 'Idle';
    }
  }

  private sessionStatusLabel(session: IHarnessSessionMeta): string {
    return session.statusLabel?.trim() || this.sessionStateLabel(session.state ?? 'normal');
  }

  private sessionStateIcon(state: NonNullable<IHarnessSessionMeta['state']>): string {
    switch (state) {
      case 'working': return 'lucide:CircleDashed';
      case 'external': return 'lucide:MonitorUp';
      case 'finished': return 'lucide:CircleCheck';
      case 'attention': return 'lucide:MessageCircleQuestion';
      case 'error': return 'lucide:CircleX';
      default: return 'lucide:Circle';
    }
  }

  private toggleSessionExpanded(sessionIdArg: string): void {
    const next = new Set(this.expandedSessionIds);
    if (next.has(sessionIdArg)) next.delete(sessionIdArg);
    else next.add(sessionIdArg);
    this.expandedSessionIds = next;
  }

  private toggleGroupCollapsed(groupIdArg: string): void {
    const next = new Set(this.collapsedGroupIds);
    if (next.has(groupIdArg)) {
      next.delete(groupIdArg);
    } else {
      next.add(groupIdArg);
    }
    this.collapsedGroupIds = next;
  }

  private handlePointerDown(
    eventArg: PointerEvent,
    refArg: IHarnessSessionListItemRef,
    groupIdArg: string | null,
    logicalIndexArg: number,
  ): void {
    if (
      this.pointerDragGesture
      || eventArg.button !== 0
      || !eventArg.isPrimary
      || logicalIndexArg < 0
    ) return;
    const handle = eventArg.currentTarget as HTMLElement;
    const source = handle.closest<HTMLElement>('.item, .resourceRow');
    if (!source) return;
    const sourceRect = source.getBoundingClientRect();
    this.pointerDragGesture = {
      pointerId: eventArg.pointerId,
      handle,
      source,
      item: itemRef(refArg),
      sourceGroupId: groupIdArg,
      sourceLogicalIndex: logicalIndexArg,
      startClientX: eventArg.clientX,
      startClientY: eventArg.clientY,
      grabOffsetX: eventArg.clientX - sourceRect.left,
      grabOffsetY: eventArg.clientY - sourceRect.top,
      phase: 'pending',
    };
    window.addEventListener('keydown', this.handleDragKeyDown, true);
    window.addEventListener('blur', this.handleDragWindowBlur);
    try {
      handle.setPointerCapture(eventArg.pointerId);
    } catch {
      this.finishPointerDrag(false);
    }
  }

  private readonly handlePointerMove = (eventArg: PointerEvent): void => {
    const gesture = this.pointerDragGesture;
    if (!gesture || eventArg.pointerId !== gesture.pointerId) return;
    if (!gesture.source.isConnected || !gesture.handle.isConnected) {
      this.finishPointerDrag(false);
      return;
    }
    if (
      gesture.phase === 'pending'
      && Math.hypot(
        eventArg.clientX - gesture.startClientX,
        eventArg.clientY - gesture.startClientY,
      ) < dragThresholdPixels
    ) return;
    if (gesture.phase === 'pending' && !this.startPointerDrag(eventArg, gesture)) return;
    eventArg.preventDefault();
    this.moveDragPreview(eventArg.clientX, eventArg.clientY, gesture);
    this.dragAutoScrollClientX = eventArg.clientX;
    this.dragAutoScrollClientY = eventArg.clientY;
    this.updateDragAutoScrollVelocity();
    this.updateDropSlotFromPointer();
  };

  private readonly handlePointerUp = (eventArg: PointerEvent): void => {
    const gesture = this.pointerDragGesture;
    if (!gesture || eventArg.pointerId !== gesture.pointerId) return;
    if (gesture.phase === 'active') {
      this.dragAutoScrollClientX = eventArg.clientX;
      this.dragAutoScrollClientY = eventArg.clientY;
      this.moveDragPreview(eventArg.clientX, eventArg.clientY, gesture);
      this.updateDropSlotFromPointer();
      this.finishPointerDrag(true);
      return;
    }
    this.finishPointerDrag(false);
  };

  private readonly handlePointerCancel = (eventArg: PointerEvent): void => {
    if (eventArg.pointerId === this.pointerDragGesture?.pointerId) this.finishPointerDrag(false);
  };

  private readonly handleLostPointerCapture = (eventArg: PointerEvent): void => {
    if (eventArg.pointerId === this.pointerDragGesture?.pointerId) this.finishPointerDrag(false);
  };

  private readonly handleDragKeyDown = (eventArg: KeyboardEvent): void => {
    if (eventArg.key !== 'Escape' || !this.pointerDragGesture) return;
    eventArg.preventDefault();
    eventArg.stopPropagation();
    this.finishPointerDrag(false);
  };

  private readonly handleDragWindowBlur = (): void => {
    this.finishPointerDrag(false);
  };

  private startPointerDrag(eventArg: PointerEvent, gestureArg: IPointerDragGesture): boolean {
    const sourceRect = gestureArg.source.getBoundingClientRect();
    gestureArg.source.classList.add('dragging');
    gestureArg.source.style.setProperty('--drag-preview-width', `${sourceRect.width}px`);
    gestureArg.source.style.setProperty('--drag-preview-height', `${sourceRect.height}px`);
    this.moveDragPreview(eventArg.clientX, eventArg.clientY, gestureArg);
    gestureArg.source.setAttribute('popover', 'manual');
    try {
      gestureArg.source.showPopover();
    } catch {
      this.finishPointerDrag(false);
      return false;
    }
    gestureArg.phase = 'active';
    this.reflowGeneration += 1;
    this.cancelReflowAnimations();
    this.draggedItemHeight = sourceRect.height;
    this.draggingItem = itemRef(gestureArg.item);
    const remainingCount = this.sortableItems(this.itemsForGroup(gestureArg.sourceGroupId)).length;
    this.dropSlot = {
      groupId: gestureArg.sourceGroupId,
      index: Math.min(gestureArg.sourceLogicalIndex, remainingCount),
    };
    return true;
  }

  private moveDragPreview(
    clientXArg: number,
    clientYArg: number,
    gestureArg: IPointerDragGesture,
  ): void {
    gestureArg.source.style.setProperty(
      '--drag-preview-x',
      `${clientXArg - gestureArg.grabOffsetX}px`,
    );
    gestureArg.source.style.setProperty(
      '--drag-preview-y',
      `${clientYArg - gestureArg.grabOffsetY}px`,
    );
  }

  private setDropSlot(groupIdArg: string | null, indexArg: number): void {
    const current = this.dropSlot;
    if (
      current
      && current.groupId === groupIdArg
      && current.index === indexArg
      && !this.dropAttachTarget
    ) return;
    this.dropAttachTarget = undefined;
    const previousRects = this.captureRowRects();
    const generation = ++this.reflowGeneration;
    this.dropSlot = { groupId: groupIdArg, index: indexArg };
    void this.animateRowReflow(previousRects, generation);
  }

  private setDropAttachTarget(targetArg: IHarnessSessionListItemRef): void {
    if (sameItem(this.dropAttachTarget ?? null, targetArg) && !this.dropSlot) return;
    const previousRects = this.captureRowRects();
    const generation = ++this.reflowGeneration;
    this.dropAttachTarget = itemRef(targetArg);
    // An attaching pointer closes any open insertion gap, so the list stops offering both.
    this.dropSlot = undefined;
    void this.animateRowReflow(previousRects, generation);
  }

  private clearDropSlot(): void {
    this.dropAttachTarget = undefined;
    if (!this.dropSlot) return;
    const previousRects = this.captureRowRects();
    const generation = ++this.reflowGeneration;
    this.dropSlot = undefined;
    void this.animateRowReflow(previousRects, generation);
  }

  private updateDragAutoScrollVelocity(): void {
    const container = this.scrollContainer;
    const gesture = this.pointerDragGesture;
    if (!container || gesture?.phase !== 'active') return;
    const rect = container.getBoundingClientRect();
    if (this.dragAutoScrollClientX < rect.left || this.dragAutoScrollClientX > rect.right) {
      this.stopDragAutoScroll();
      return;
    }
    const edgeSize = Math.min(64, Math.max(32, rect.height * 0.25));
    const topDepth = Math.max(
      0,
      Math.min(1, (rect.top + edgeSize - this.dragAutoScrollClientY) / edgeSize),
    );
    const bottomDepth = Math.max(
      0,
      Math.min(1, (this.dragAutoScrollClientY - (rect.bottom - edgeSize)) / edgeSize),
    );
    this.dragAutoScrollVelocity = bottomDepth > 0
      ? dragAutoScrollMaxPixelsPerSecond * bottomDepth * bottomDepth
      : topDepth > 0
        ? -dragAutoScrollMaxPixelsPerSecond * topDepth * topDepth
        : 0;
    if (this.dragAutoScrollVelocity === 0) {
      this.stopDragAutoScroll();
      return;
    }
    if (this.dragAutoScrollFrame === undefined) {
      this.dragAutoScrollLastFrame = performance.now();
      this.dragAutoScrollFrame = requestAnimationFrame(this.runDragAutoScroll);
    }
  }

  private readonly runDragAutoScroll = (timestampArg: number): void => {
    this.dragAutoScrollFrame = undefined;
    const container = this.scrollContainer;
    const gesture = this.pointerDragGesture;
    if (!container || gesture?.phase !== 'active' || this.dragAutoScrollVelocity === 0) return;
    const elapsedMs = Math.min(34, Math.max(0, timestampArg - this.dragAutoScrollLastFrame));
    this.dragAutoScrollLastFrame = timestampArg;
    const previousScrollTop = container.scrollTop;
    container.scrollTop += this.dragAutoScrollVelocity * elapsedMs / 1000;
    this.syncScrollShadowState();
    const atScrollBoundary = this.dragAutoScrollVelocity < 0
      ? container.scrollTop <= 0
      : container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
    if (container.scrollTop === previousScrollTop && atScrollBoundary) {
      this.stopDragAutoScroll();
      return;
    }
    this.updateDropSlotFromPointer();
    this.dragAutoScrollFrame = requestAnimationFrame(this.runDragAutoScroll);
  };

  private updateDropSlotFromPointer(): void {
    const pointerX = this.dragAutoScrollClientX;
    const pointerY = this.dragAutoScrollClientY;
    const placeholder = this.shadowRoot?.querySelector<HTMLElement>('.dropPlaceholder');
    if (placeholder) {
      const rect = placeholder.getBoundingClientRect();
      if (
        pointerX >= rect.left
        && pointerX <= rect.right
        && pointerY >= rect.top
        && pointerY <= rect.bottom
      ) {
        this.setDropSlot(
          placeholder.dataset.dropGroupId || null,
          Number(placeholder.dataset.dropIndex),
        );
        return;
      }
    }
    const selector = '[data-drop-group-id][data-drop-index][data-drop-kind]';
    const dragging = this.draggingItem;
    let target: HTMLElement | undefined;
    const offsets = this.dragAutoScrollVelocity === 0
      ? [0, -8, 8, -16, 16, -32, 32]
      : [0, 8, 16, 32, 48, 64, 96].map(
          (value) => value * (this.dragAutoScrollVelocity < 0 ? 1 : -1),
        );
    for (const offset of offsets) {
      const hit = this.shadowRoot?.elementFromPoint(pointerX, pointerY + offset);
      const candidate = hit?.closest<HTMLElement>(selector);
      if (
        candidate
        && !(
          dragging
          && candidate.dataset.itemKind === dragging.kind
          && candidate.dataset.itemId === dragging.id
        )
        && Number(candidate.dataset.dropIndex) >= 0
      ) {
        target = candidate;
        break;
      }
    }
    if (!target) {
      this.clearDropSlot();
      return;
    }
    const baseIndex = Number(target.dataset.dropIndex);
    const groupId = target.dataset.dropGroupId || null;
    const rect = target.getBoundingClientRect();
    const attachTarget = this.attachTargetForDrop(target, pointerY, rect);
    if (attachTarget) {
      this.setDropAttachTarget(attachTarget);
      return;
    }
    const index = target.dataset.dropKind === 'card' && pointerY >= rect.top + rect.height / 2
      ? baseIndex + 1
      : baseIndex;
    this.setDropSlot(groupId, index);
  }

  /**
   * A resource released on the centre of an eligible row attaches to it. Only a row qualifies —
   * never an insertion slot — and only while a resource is the dragged item. A resource that is
   * already attached to several items is excluded: a drag cannot name which attachment would
   * move, so such a row offers no affordance and the drop stays purely positional. Moving one of
   * several attachments is a menu action.
   */
  private attachTargetForDrop(
    targetArg: HTMLElement,
    pointerYArg: number,
    rectArg: DOMRect,
  ): IHarnessSessionListItemRef | undefined {
    const dragging = this.draggingItem;
    if (!dragging || dragging.kind !== 'resource') return undefined;
    if (this.resourceTargets(dragging.id).length > 1) return undefined;
    if (targetArg.dataset.dropKind !== 'card') return undefined;
    const kind = targetArg.dataset.itemKind;
    const id = targetArg.dataset.itemId;
    if ((kind !== 'session' && kind !== 'resource') || !id) return undefined;
    const candidate: IHarnessSessionListItemRef = { kind, id };
    if (sameItem(candidate, dragging)) return undefined;
    if (!this.isResourceTargetEligible(dragging.id, candidate)) return undefined;
    // The ratio is the whole band, so each side of the centre gets half of it.
    const halfBand = (rectArg.height * attachCentreBandRatio) / 2;
    const centre = rectArg.top + rectArg.height / 2;
    if (Math.abs(pointerYArg - centre) > halfBand) return undefined;
    return candidate;
  }

  private get scrollContainer(): HTMLElement | undefined {
    return this.shadowRoot?.querySelector<HTMLElement>('.list') ?? undefined;
  }

  private observeScrollGeometry(): void {
    if (!this.isConnected) return;
    const container = this.scrollContainer;
    const content = this.shadowRoot?.querySelector<HTMLElement>('.listContent') ?? undefined;
    if (!container || !content) return;
    if (
      this.scrollResizeObserver
      && this.observedScrollContainer === container
      && this.observedScrollContent === content
    ) return;
    this.scrollResizeObserver?.disconnect();
    this.scrollResizeObserver = new ResizeObserver(this.syncScrollShadowState);
    this.scrollResizeObserver.observe(container);
    this.scrollResizeObserver.observe(content);
    this.observedScrollContainer = container;
    this.observedScrollContent = content;
  }

  private readonly syncScrollShadowState = (): void => {
    const container = this.scrollContainer;
    if (!container) return;
    const topVisible = container.scrollTop > 1;
    const bottomVisible = container.scrollTop + container.clientHeight < container.scrollHeight - 1;
    if (this.topScrollShadowVisible !== topVisible) this.topScrollShadowVisible = topVisible;
    if (this.bottomScrollShadowVisible !== bottomVisible) this.bottomScrollShadowVisible = bottomVisible;
  };

  private stopDragAutoScroll(): void {
    if (this.dragAutoScrollFrame !== undefined) cancelAnimationFrame(this.dragAutoScrollFrame);
    this.dragAutoScrollFrame = undefined;
    this.dragAutoScrollVelocity = 0;
    this.dragAutoScrollLastFrame = 0;
  }

  private createMoveDetail(
    refArg: IHarnessSessionListItemRef,
    toGroupIdArg: string | null,
    indexArg: number,
    sourceArg: THarnessItemMoveSource,
  ): IHarnessItemMoveDetail {
    const targetItems = this.sortableItems(this.itemsForGroup(toGroupIdArg));
    const beforeItem = targetItems[Math.max(0, indexArg)];
    return {
      item: itemRef(refArg),
      fromGroupId: this.groupIdForItem(refArg),
      toGroupId: toGroupIdArg,
      beforeItem: beforeItem ? itemRef(beforeItem) : null,
      source: sourceArg,
    };
  }

  private emitItemMove(detailArg: IHarnessItemMoveDetail): void {
    this.dispatchEvent(
      new CustomEvent('harness-item-move', { detail: detailArg, bubbles: true, composed: true }),
    );
  }

  private handleKeyboardMove(
    eventArg: KeyboardEvent,
    refArg: IHarnessSessionListItemRef,
    titleArg: string,
    groupIdArg: string | null,
  ): void {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(eventArg.key)) return;
    eventArg.preventDefault();
    eventArg.stopPropagation();
    const items = this.sortableItems(this.itemsForGroup(groupIdArg), false);
    const sourceIndex = items.findIndex((item) => sameItem(item, refArg));
    if (sourceIndex < 0) return;
    const sectionIds: Array<string | null> = [...this.effectiveGroups.map((group) => group.id), null];
    const sectionIndex = sectionIds.indexOf(groupIdArg);
    const adjacentGroupId = eventArg.key === 'ArrowUp' && sourceIndex === 0 && sectionIndex > 0
      ? sectionIds[sectionIndex - 1]
      : eventArg.key === 'ArrowDown'
          && sourceIndex === items.length - 1
          && sectionIndex >= 0
          && sectionIndex < sectionIds.length - 1
        ? sectionIds[sectionIndex + 1]
        : undefined;
    if (adjacentGroupId !== undefined) {
      const targetItems = this.sortableItems(this.itemsForGroup(adjacentGroupId), false);
      const targetIndex = eventArg.key === 'ArrowUp' ? targetItems.length : 0;
      const beforeItem = targetItems[targetIndex];
      const groupName = adjacentGroupId === null
        ? 'Ungrouped'
        : this.effectiveGroups.find((group) => group.id === adjacentGroupId)?.name ?? adjacentGroupId;
      this.moveAnnouncement = `Moved ${titleArg} to ${groupName}.`;
      this.keyboardMoveFocus = { item: itemRef(refArg), groupId: adjacentGroupId };
      this.emitItemMove({
        item: itemRef(refArg),
        fromGroupId: groupIdArg,
        toGroupId: adjacentGroupId,
        beforeItem: beforeItem ? itemRef(beforeItem) : null,
        source: 'keyboard',
      });
      return;
    }
    const remaining = items.filter((item) => !sameItem(item, refArg));
    const targetIndex = eventArg.key === 'Home'
      ? 0
      : eventArg.key === 'End'
        ? remaining.length
        : eventArg.key === 'ArrowUp'
          ? Math.max(0, sourceIndex - 1)
          : Math.min(remaining.length, sourceIndex + 1);
    if (targetIndex === sourceIndex) return;
    const beforeItem = remaining[targetIndex];
    this.moveAnnouncement = beforeItem
      ? `Moved ${titleArg} before ${beforeItem.title}.`
      : `Moved ${titleArg} to the end.`;
    this.emitItemMove({
      item: itemRef(refArg),
      fromGroupId: groupIdArg,
      toGroupId: groupIdArg,
      beforeItem: beforeItem ? itemRef(beforeItem) : null,
      source: 'keyboard',
    });
  }

  private rowKey(rowArg: HTMLElement): string {
    return `${rowArg.dataset.itemKind}:${rowArg.dataset.itemId}`;
  }

  private captureRowRects(): Map<string, DOMRect> {
    const result = new Map<string, DOMRect>();
    const draggingKey = this.draggingItem ? itemKey(this.draggingItem) : '';
    for (const row of this.shadowRoot?.querySelectorAll<HTMLElement>('[data-item-kind][data-item-id]') ?? []) {
      const key = this.rowKey(row);
      if (key === draggingKey) continue;
      result.set(key, row.getBoundingClientRect());
    }
    return result;
  }

  private restoreKeyboardMoveFocus(): void {
    const pending = this.keyboardMoveFocus;
    if (!pending) return;
    if (this.groupIdForItem(pending.item) !== pending.groupId) return;
    const handle = this.rowElementFor(pending.item)?.querySelector<HTMLElement>('.dragHandle');
    if (handle) {
      handle.focus();
      this.keyboardMoveFocus = undefined;
      return;
    }
    const groupHeader = Array.from(
      this.shadowRoot?.querySelectorAll<HTMLElement>('.groupHeader[data-drop-group-id]') ?? [],
    ).find((candidate) => candidate.dataset.dropGroupId === (pending.groupId ?? ''));
    if (groupHeader) {
      groupHeader.focus();
      this.keyboardMoveFocus = undefined;
    }
  }

  /** Known, de-duplicated targets the host allows; an absent entry means unrestricted. */
  private eligibleTargets(resourceIdArg: string): IHarnessSessionListItemRef[] | undefined {
    const declared = this.eligibleTargetsByResourceId[resourceIdArg];
    if (!declared) return undefined;
    const itemsByKey = this.itemsByKey;
    const result: IHarnessSessionListItemRef[] = [];
    const seen = new Set<string>();
    for (const target of declared) {
      const key = target && itemKey(target);
      if (
        !key
        || seen.has(key)
        || !itemsByKey.has(key)
        || sameItem(target, { kind: 'resource', id: resourceIdArg })
      ) continue;
      seen.add(key);
      result.push(itemRef(target));
    }
    return result;
  }

  private isResourceTargetEligible(
    resourceIdArg: string,
    targetArg: IHarnessSessionListItemRef,
  ): boolean {
    const eligible = this.eligibleTargets(resourceIdArg);
    return eligible === undefined || eligible.some((candidate) => sameItem(candidate, targetArg));
  }

  private reconcilePendingResourceAssociation(): void {
    const pending = this.pendingResourceAssociationRequest;
    if (!pending) return;
    if (!this.resourcesById.has(pending.resourceId)) {
      this.pendingResourceAssociationRequest = undefined;
      return;
    }
    const current = this.resourceTargets(pending.resourceId);
    const sameSet = (leftArg: IHarnessSessionListItemRef[], rightArg: IHarnessSessionListItemRef[]): boolean =>
      leftArg.length === rightArg.length
      && leftArg.every((candidate) => rightArg.some((other) => sameItem(candidate, other)));
    if (!sameSet(current, pending.expectedTargets)) {
      // Still the pre-request set: the host has not answered yet, so keep waiting.
      if (sameSet(current, this.pendingRequestPriorTargets(pending))) return;
      const controlledTitle = current.length
        ? current.map((target) => this.itemTitleFor(target)).join(', ')
        : 'unattached';
      this.moveAnnouncement = `Association request for ${pending.resourceTitle} was not applied; controlled attachments are ${controlledTitle}.`;
      this.pendingResourceAssociationRequest = undefined;
      return;
    }
    const fromTitle = pending.fromTarget ? this.itemTitleFor(pending.fromTarget) : '';
    const toTitle = pending.toTarget ? this.itemTitleFor(pending.toTarget) : '';
    this.moveAnnouncement = pending.mode === 'attach' || pending.mode === 'attach-additional'
      ? `${pending.resourceTitle} is now attached to ${toTitle}.`
      : pending.mode === 'detach'
        ? `${pending.resourceTitle} is now detached from ${fromTitle}.`
        : `${pending.resourceTitle} is now moved from ${fromTitle} to ${toTitle}.`;
    this.pendingResourceAssociationRequest = undefined;
  }

  /** The set the request started from, reconstructed from the expected set and the request. */
  private pendingRequestPriorTargets(
    pendingArg: IPendingResourceAssociationRequest,
  ): IHarnessSessionListItemRef[] {
    if (pendingArg.mode === 'attach' || pendingArg.mode === 'attach-additional') {
      return pendingArg.expectedTargets.filter((candidate) => !sameItem(candidate, pendingArg.toTarget));
    }
    if (pendingArg.mode === 'detach') {
      return [...pendingArg.expectedTargets, pendingArg.fromTarget!];
    }
    return pendingArg.expectedTargets.map(
      (candidate) => (sameItem(candidate, pendingArg.toTarget) ? pendingArg.fromTarget! : candidate),
    );
  }

  private resourceAssociationAnnouncement(
    detailArg: THarnessResourceAssociateDetail,
    completedArg: boolean,
  ): string {
    const resourceTitle = detailArg.resource.title || detailArg.resource.id;
    const fromTitle = detailArg.fromTarget ? this.itemTitleFor(detailArg.fromTarget) : '';
    const toTitle = detailArg.toTarget ? this.itemTitleFor(detailArg.toTarget) : '';
    const verb = completedArg ? 'completed' : 'requested';
    return detailArg.mode === 'attach'
      ? `Attach ${verb} for ${resourceTitle} to ${toTitle}.`
      : detailArg.mode === 'attach-additional'
        ? `Additional attach ${verb} for ${resourceTitle} to ${toTitle}.`
        : detailArg.mode === 'detach'
          ? `Detach ${verb} for ${resourceTitle} from ${fromTitle}.`
          : `Move ${verb} for ${resourceTitle} from ${fromTitle} to ${toTitle}.`;
  }

  private itemTitleFor(refArg: IHarnessSessionListItemRef): string {
    return this.itemsByKey.get(itemKey(refArg))?.title ?? refArg.id;
  }

  private emitSession(nameArg: string, sessionArg: IHarnessSessionMeta): void {
    const detail: IHarnessSessionEventDetail = { session: sessionArg };
    this.dispatchEvent(new CustomEvent(nameArg, { detail, bubbles: true, composed: true }));
  }

  private emitResource(
    nameArg: string,
    resourceArg: IHarnessResourceMeta,
    targetsArg: IHarnessSessionListItemRef[],
  ): void {
    const detail: IHarnessResourceEventDetail = {
      resource: resourceArg,
      targets: targetsArg,
    };
    this.dispatchEvent(new CustomEvent(nameArg, { detail, bubbles: true, composed: true }));
  }

  private handleResourceContextKey(
    eventArg: KeyboardEvent,
    resourceArg: IHarnessResourceMeta,
    targetsArg: IHarnessSessionListItemRef[],
  ): void {
    if (!(eventArg.key === 'ContextMenu' || (eventArg.key === 'F10' && eventArg.shiftKey))) return;
    eventArg.preventDefault();
    eventArg.stopPropagation();
    this.emitResourceContext(
      eventArg,
      resourceArg,
      targetsArg,
      'keyboard',
      eventArg.currentTarget as HTMLElement,
    );
  }

  private emitResourceContext(
    eventArg: MouseEvent | KeyboardEvent,
    resourceArg: IHarnessResourceMeta,
    targetsArg: IHarnessSessionListItemRef[],
    sourceArg: THarnessResourceContextSource,
    focusTargetArg: HTMLElement,
  ): void {
    const rect = focusTargetArg.getBoundingClientRect();
    const contextGeneration = ++this.resourceContextGeneration;
    this.resourceContextResourceId = resourceArg.id;
    this.resourceContextFocusTarget = focusTargetArg;
    this.resourceContextFocusClass = [
      'resourceActions',
      'resourcePrimary',
      'resourceDragHandle',
    ].find((className) => focusTargetArg.classList.contains(className)) ?? 'resourcePrimary';
    const detail: IHarnessResourceContextDetail = {
      resource: resourceArg,
      targets: targetsArg,
      clientX: sourceArg === 'pointer' && eventArg instanceof MouseEvent
        ? eventArg.clientX
        : rect.left + rect.width / 2,
      clientY: sourceArg === 'pointer' && eventArg instanceof MouseEvent
        ? eventArg.clientY
        : rect.bottom,
      source: sourceArg,
      originalEvent: eventArg,
      requestAssociation: (toTargetArg, modeArg, fromTargetArg) => this.requestResourceAssociation(
        resourceArg.id,
        toTargetArg,
        sourceArg === 'keyboard' ? 'keyboard' : 'action',
        modeArg,
        fromTargetArg,
      ),
      close: () => this.closeResourceContext(contextGeneration),
    };
    this.dispatchEvent(
      new CustomEvent('harness-resource-context', { detail, bubbles: true, composed: true }),
    );
  }

  private closeResourceContext(contextGenerationArg: number): void {
    if (
      !this.isConnected
      || contextGenerationArg !== this.resourceContextGeneration
    ) return;
    const focusTarget = this.resourceContextFocusTarget;
    const resourceId = this.resourceContextResourceId;
    const focusClass = this.resourceContextFocusClass;
    this.clearResourceContextState();
    const closeGeneration = this.resourceContextGeneration;
    void this.updateComplete.then(() => {
      if (!this.isConnected || closeGeneration !== this.resourceContextGeneration) return;
      if (focusTarget?.isConnected) {
        focusTarget.focus();
        return;
      }
      const row = this.rowElementFor({ kind: 'resource', id: resourceId });
      row?.querySelector<HTMLElement>(`.${focusClass || 'resourcePrimary'}`)?.focus();
    });
  }

  private clearResourceContextState(): void {
    this.resourceContextGeneration += 1;
    this.resourceContextResourceId = '';
    this.resourceContextFocusTarget = undefined;
    this.resourceContextFocusClass = '';
  }

  private async animateRowReflow(
    previousRectsArg: Map<string, DOMRect>,
    generationArg: number,
  ): Promise<void> {
    await this.updateComplete;
    if (
      generationArg !== this.reflowGeneration
      || !this.isConnected
      || globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) return;
    this.cancelReflowAnimations();
    const draggingKey = this.draggingItem ? itemKey(this.draggingItem) : '';
    for (const row of this.shadowRoot?.querySelectorAll<HTMLElement>('[data-item-kind][data-item-id]') ?? []) {
      const key = this.rowKey(row);
      if (key === draggingKey) continue;
      const previous = previousRectsArg.get(key);
      if (!previous) continue;
      const current = row.getBoundingClientRect();
      const deltaY = previous.top - current.top;
      if (Math.abs(deltaY) < 0.5) continue;
      const animation = row.animate(
        [
          { transform: `translateY(${deltaY}px)` },
          { transform: 'translateY(0)' },
        ],
        { duration: 180, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      );
      this.reflowAnimations.add(animation);
      void animation.finished.catch(() => undefined).finally(() => {
        this.reflowAnimations.delete(animation);
      });
    }
  }

  private cancelReflowAnimations(): void {
    for (const animation of this.reflowAnimations) animation.cancel();
    this.reflowAnimations.clear();
  }

  private finishPointerDrag(commitArg: boolean): void {
    const gesture = this.pointerDragGesture;
    if (!gesture) return;
    const committing = commitArg && gesture.phase === 'active';
    // A centred release attaches instead of reordering; the two are never both pending.
    const attachTarget = committing && gesture.item.kind === 'resource'
      ? this.dropAttachTarget
      : undefined;
    const moveDetail = (committing && !attachTarget && this.dropSlot)
      ? this.createMoveDetail(gesture.item, this.dropSlot.groupId, this.dropSlot.index, 'pointer')
      : undefined;
    this.pointerDragGesture = undefined;
    window.removeEventListener('keydown', this.handleDragKeyDown, true);
    window.removeEventListener('blur', this.handleDragWindowBlur);
    this.stopDragAutoScroll();
    this.reflowGeneration += 1;
    this.cancelReflowAnimations();
    if (gesture.source.matches(':popover-open')) gesture.source.hidePopover();
    gesture.source.removeAttribute('popover');
    gesture.source.classList.remove('dragging');
    for (const property of [
      '--drag-preview-width',
      '--drag-preview-height',
      '--drag-preview-x',
      '--drag-preview-y',
    ]) gesture.source.style.removeProperty(property);
    this.draggingItem = undefined;
    this.draggedItemHeight = 0;
    this.dropSlot = undefined;
    this.dropAttachTarget = undefined;
    try {
      if (gesture.handle.hasPointerCapture(gesture.pointerId)) {
        gesture.handle.releasePointerCapture(gesture.pointerId);
      }
    } catch {
      // Capture may already be released or the handle may have disconnected.
    }
    if (attachTarget) {
      // Only an unattached or singly attached resource can reach this point, so the mode follows
      // from the current set without asking anything further.
      const current = this.resourceTargets(gesture.item.id);
      this.requestResourceAssociation(
        gesture.item.id,
        attachTarget,
        'pointer',
        current.length === 0 ? 'attach' : 'move',
        current[0],
      );
      return;
    }
    if (moveDetail) this.emitItemMove(moveDetail);
  }
}
